import { colors } from '@lmc/common/constants'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { BarChart } from '../../../../components/charts'
import {
  HeaderSection,
  NoDataFound,
  Spinner,
  Table
} from '../../../../components/generic'
import Operations from '../../../../operations'
import { GroupsSidebarNavigation } from '../../components/reports'
import {
  getHighChartsSeries,
  getTableData,
  orderByTotal
} from '../../utils/groupReports'

const renderEmpty = () => (
  <div style={{ paddingTop: 5 }}>
    <NoDataFound
      heading="Nothing to see here..."
      text="Your group has no BMI data to display."
    />
  </div>
)
const renderLoading = () => (
  <p>
    <Spinner />
  </p>
)

export const renderContent = (data, sort, setSort) => {
  const categories = [
    {
      key: 'healthy',
      label: 'Healthy',
      color: colors.green,
      icon: { ...styles.headerIcons.roundedSquare, color: colors.green }
    },
    {
      key: 'underweight',
      label: 'Underweight',
      color: colors.blue,
      icon: { ...styles.headerIcons.roundedSquare, color: colors.blue }
    },
    {
      key: 'overweight',
      label: 'Overweight',
      color: colors.yellow,
      icon: { ...styles.headerIcons.roundedSquare, color: colors.yellow }
    },
    {
      key: 'obese',
      label: 'Obese',
      color: colors.orange,
      icon: { ...styles.headerIcons.roundedSquare, color: colors.orange }
    },
    {
      key: 'extremelyObese',
      label: 'Extremely Obese',
      color: colors.red,
      icon: { ...styles.headerIcons.roundedSquare, color: colors.red }
    }
  ]

  const columns = [
    {
      label: 'Site',
      sortable: false
    },
    ...categories.map((category) => ({
      label: category.label,
      key: category.key,
      type: 'percentage',
      icon: category.icon,
      color: category.color
    }))
  ]

  const sortedData = orderByTotal(data, sort)
  const series = getHighChartsSeries(sortedData, categories)

  return (
    <div style={{ paddingTop: '-30px' }}>
      <BarChart
        type="bar"
        height={200 + sortedData.length * 40}
        categories={sortedData.map((d) => (d.keyDoc ? d.keyDoc.name : d.key))}
        series={series.reverse()}
        stacked
        legend={{ reversed: true }}
        yAxisTickFormat={'{value}%'}
        yAxisTickInterval={25}
        symbolRadius={0}
      />
      <Table
        data={getTableData(sortedData, categories, MAX_NAME_LENGTH)}
        defaultSorted={{ title: 'Healthy', sorted: 'asc' }}
        onSortChange={(newSort) => setSort(newSort)}
        columns={columns}
      />
    </div>
  )
}

export function GroupBMIReports(props) {
  const { fetchLists, isLoading, data } = props

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchLists()
  }, [])

  const [sort, setSort] = useState({ key: 'healthy', order: 'asc' })

  return (
    <div style={styles.mainContainer}>
      <StyledNavigation>
        <GroupsSidebarNavigation />
      </StyledNavigation>
      <StyledInnerContainer>
        <HeaderSection title="Today's BMI" />
        {isLoading
          ? renderLoading()
          : !data || !data.length
            ? renderEmpty()
            : renderContent(data, sort, setSort)}
      </StyledInnerContainer>
    </div>
  )
}

const MAX_NAME_LENGTH = 20

const styles = {
  headerIcons: {
    roundedSquare: {
      name: 'roundedSquare',
      size: 8,
      viewBox: '0 0 8 8',
      marginTop: 8
    }
  },
  mainContainer: {
    display: 'flex',
    height: 'calc(100vh - 64px)',
    maxWidth: '2000px'
  }
}

GroupBMIReports.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool
}

const mapStateToProps = (state) => {
  return {
    isLoading: get(state, 'data.requests.group_bmi.isLoading'),
    error: get(state, 'data.requests.group_bmi.error'),
    data: get(state, 'data.requests.group_bmi.response')
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchLists: () => {
      dispatch(
        Operations.fetchData({
          url: 'aggregations/bmi',
          // query: {},
          id: 'group_bmi'
        })
      )
    }
  }
}

const StyledInnerContainer = styled.div`
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 24px;
  height: 100%;
  width: 100%
`

const StyledNavigation = styled.div`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    display: none;
  }
`

export default connect(mapStateToProps, mapDispatchToProps)(GroupBMIReports)
